/**
 * AUTH-04..AUTH-10 : inscription B2B multi-étapes, vérification d'e-mail (activation AUTOMATIQUE si contrôles conformes —
 * décision validée), suivi de demande, mot de passe oublié, réinitialisation, acceptation d'invitation.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Alert, Button, Card, Choice, Steps, TextField, type Etape } from '@myron/design-system';
import { ApiErreur, messageErreur, post } from '../../../shared/api/client';
import { MentionSimule, StatutBadge } from '../../../shared/ui/composants';

// ───────────────────────────────── Inscription ─────────────────────────────────
interface PreVerification { formatValide: boolean; etablissementActif?: boolean; raisonSociale?: string; codeNaf?: string; nafAutorise?: boolean; doublon: boolean; indisponible: boolean }
interface Dossier {
  siret: string; raisonSociale: string; nomCommercial: string; tvaIntracom: string;
  prenom: string; nom: string; email: string; telephone: string;
  ligne1: string; codePostal: string; ville: string;
  motDePasse: string; confirmation: string; accepteConditions: boolean;
}
const VIDE: Dossier = { siret: '', raisonSociale: '', nomCommercial: '', tvaIntracom: '', prenom: '', nom: '', email: '', telephone: '', ligne1: '', codePostal: '', ville: '', motDePasse: '', confirmation: '', accepteConditions: false };
const ETAPES = ['Entreprise', 'Contact', 'Facturation', 'Sécurité'] as const;

function validerEtape(i: number, d: Dossier): Record<string, string> {
  const e: Record<string, string> = {};
  if (i === 0) {
    if (!/^[0-9]{14}$/.test(d.siret.replace(/\s/g, ''))) e.siret = 'Le SIRET comporte 14 chiffres';
    if (!d.raisonSociale.trim()) e.raisonSociale = 'Raison sociale requise';
    if (d.tvaIntracom && !/^FR[0-9A-Z]{2}[0-9]{9}$/.test(d.tvaIntracom.replace(/\s/g, ''))) e.tvaIntracom = 'Format attendu : FR + 11 caractères';
  }
  if (i === 1) {
    if (!d.prenom.trim()) e.prenom = 'Prénom requis';
    if (!d.nom.trim()) e.nom = 'Nom requis';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email)) e.email = 'Adresse e-mail invalide';
  }
  if (i === 2) {
    if (!d.ligne1.trim()) e.ligne1 = 'Adresse requise';
    if (!/^[0-9]{5}$/.test(d.codePostal)) e.codePostal = '5 chiffres';
    if (!d.ville.trim()) e.ville = 'Ville requise';
  }
  if (i === 3) {
    if (d.motDePasse.length < 12) e.motDePasse = '12 caractères minimum';
    if (d.confirmation !== d.motDePasse) e.confirmation = 'Les mots de passe ne correspondent pas';
    if (!d.accepteConditions) e.accepteConditions = 'Acceptation obligatoire';
  }
  return e;
}
const ETAPE_DU_CHAMP: Record<string, number> = { siret: 0, raisonSociale: 0, tvaIntracom: 0, prenom: 1, nom: 1, email: 1, telephone: 1, adresseFacturation: 2, motDePasse: 3, accepteConditions: 3 };

export function PageDevenirClient() {
  const [etape, setEtape] = useState(0);
  const [d, setD] = useState<Dossier>(VIDE);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [pre, setPre] = useState<PreVerification | null>(null);
  const [verif, setVerif] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreurApi, setErreurApi] = useState<unknown>(null);
  const [resultat, setResultat] = useState<{ reference: string; jetonSuivi: string } | null>(null);
  const maj = (k: keyof Dossier) => (e: React.ChangeEvent<HTMLInputElement>) => setD((x) => ({ ...x, [k]: k === 'accepteConditions' ? e.target.checked : e.target.value }));

  const verifierSiret = async () => {
    const siret = d.siret.replace(/\s/g, '');
    if (!/^[0-9]{14}$/.test(siret)) { setErreurs({ siret: 'Le SIRET comporte 14 chiffres' }); return; }
    setVerif(true);
    setErreurs({});
    try {
      const r = await post<PreVerification>('/inscriptions/verifier-siret', { siret });
      setPre(r);
      if (r.raisonSociale && !d.raisonSociale) setD((x) => ({ ...x, raisonSociale: r.raisonSociale! }));
    } catch (e) { setErreurApi(e); } finally { setVerif(false); }
  };

  const suivant = (e: FormEvent) => {
    e.preventDefault();
    const err = validerEtape(etape, d);
    setErreurs(err);
    if (Object.keys(err).length) return;
    if (etape < 3) { setEtape(etape + 1); return; }
    void soumettre();
  };

  const soumettre = async () => {
    setEnvoi(true);
    setErreurApi(null);
    try {
      const r = await post<{ reference: string; jetonSuivi: string }>('/inscriptions', {
        siret: d.siret.replace(/\s/g, ''), raisonSociale: d.raisonSociale.trim(), ...(d.nomCommercial ? { nomCommercial: d.nomCommercial } : {}), ...(d.tvaIntracom ? { tvaIntracom: d.tvaIntracom.replace(/\s/g, '') } : {}),
        email: d.email.trim().toLowerCase(), motDePasse: d.motDePasse, prenom: d.prenom.trim(), nom: d.nom.trim(), ...(d.telephone ? { telephone: d.telephone } : {}),
        adresseFacturation: { ligne1: d.ligne1, codePostal: d.codePostal, ville: d.ville, pays: 'FR' }, accepteConditions: true,
      });
      setResultat(r);
    } catch (e) {
      setErreurApi(e);
      if (e instanceof ApiErreur && Object.keys(e.champs).length) {
        setErreurs(e.champs);
        const premiere = Math.min(...Object.keys(e.champs).map((c) => ETAPE_DU_CHAMP[c] ?? 3));
        setEtape(premiere);
      }
    } finally { setEnvoi(false); }
  };

  if (resultat) {
    const lienStatut = `/statut-inscription?reference=${encodeURIComponent(resultat.reference)}&suivi=${encodeURIComponent(resultat.jetonSuivi)}`;
    return (
      <Card as="section" aria-labelledby="titre-insc-ok">
        <div className="my-pile">
          <h1 id="titre-insc-ok" className="my-titre-2">Vérifiez votre adresse e-mail</h1>
          <Alert ton="succes" titre={`Demande ${resultat.reference} enregistrée`}>
            Un lien de confirmation a été envoyé à <strong>{d.email}</strong>. Dès sa confirmation, les contrôles automatiques (SIRET, activité, doublon) sont lancés :
            si tout est conforme, votre compte est <strong>activé automatiquement</strong>.
          </Alert>
          <p className="my-petit my-secondaire">Simulation : le message est visible dans le panneau « Simulation » → onglet « E-mails ». <MentionSimule>E-mail simulé</MentionSimule></p>
          <Link to={lienStatut} className="my-btn my-btn--secondaire">Suivre ma demande</Link>
        </div>
      </Card>
    );
  }

  const etapes: Etape[] = ETAPES.map((t, i) => ({ titre: t, etat: i < etape ? 'fait' : i === etape ? 'encours' : 'avenir' }));
  return (
    <Card as="section" aria-labelledby="titre-insc">
      <form className="my-form" onSubmit={suivant} noValidate>
        <h1 id="titre-insc" className="my-titre-2">Ouvrir un compte professionnel</h1>
        <p className="my-secondaire">Réservé aux entreprises disposant d’un SIRET. Les prix personnalisés sont visibles après activation.</p>
        <Steps etapes={etapes} label="Étapes de l’inscription" />
        {erreurApi && !(erreurApi instanceof ApiErreur && Object.keys(erreurApi.champs).length) ? <Alert ton="erreur" titre="Inscription impossible">{messageErreur(erreurApi)}</Alert> : null}
        {erreurApi instanceof ApiErreur && Object.keys(erreurApi.champs).length ? <Alert ton="erreur" titre="La demande contient des erreurs">{Object.values(erreurApi.champs).join(' · ')}</Alert> : null}

        {etape === 0 && (
          <fieldset className="my-pile" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="my-titre-4">Entreprise</legend>
            <div className="my-form-ligne" style={{ alignItems: 'end' }}>
              <TextField label="SIRET" inputMode="numeric" required value={d.siret} onChange={maj('siret')} erreur={erreurs.siret} aide="Simulation : SIRET de test commençant par 000 (ex. 00000000900019)" />
              <Button variante="secondaire" chargement={verif} onClick={verifierSiret}>Vérifier le SIRET</Button>
            </div>
            {pre && <ResultatPreVerification pre={pre} />}
            <TextField label="Raison sociale" required value={d.raisonSociale} onChange={maj('raisonSociale')} erreur={erreurs.raisonSociale} autoComplete="organization" />
            <div className="my-form-ligne">
              <TextField label="Nom commercial (facultatif)" value={d.nomCommercial} onChange={maj('nomCommercial')} />
              <TextField label="N° de TVA intracommunautaire (facultatif)" value={d.tvaIntracom} onChange={maj('tvaIntracom')} erreur={erreurs.tvaIntracom} />
            </div>
          </fieldset>
        )}
        {etape === 1 && (
          <fieldset className="my-pile" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="my-titre-4">Contact (administrateur du compte)</legend>
            <div className="my-form-ligne">
              <TextField label="Prénom" required value={d.prenom} onChange={maj('prenom')} erreur={erreurs.prenom} autoComplete="given-name" />
              <TextField label="Nom" required value={d.nom} onChange={maj('nom')} erreur={erreurs.nom} autoComplete="family-name" />
            </div>
            <TextField label="Adresse e-mail professionnelle" type="email" required value={d.email} onChange={maj('email')} erreur={erreurs.email} autoComplete="email" />
            <TextField label="Téléphone (facultatif)" type="tel" value={d.telephone} onChange={maj('telephone')} autoComplete="tel" aide="Minimisation : seul le strict nécessaire est collecté." />
          </fieldset>
        )}
        {etape === 2 && (
          <fieldset className="my-pile" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="my-titre-4">Adresse de facturation</legend>
            <TextField label="Adresse" required value={d.ligne1} onChange={maj('ligne1')} erreur={erreurs.ligne1} autoComplete="address-line1" />
            <div className="my-form-ligne">
              <TextField label="Code postal" inputMode="numeric" required value={d.codePostal} onChange={maj('codePostal')} erreur={erreurs.codePostal} autoComplete="postal-code" />
              <TextField label="Ville" required value={d.ville} onChange={maj('ville')} erreur={erreurs.ville} autoComplete="address-level2" />
            </div>
          </fieldset>
        )}
        {etape === 3 && (
          <fieldset className="my-pile" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="my-titre-4">Sécurité et consentement</legend>
            <TextField label="Mot de passe" type="password" required value={d.motDePasse} onChange={maj('motDePasse')} erreur={erreurs.motDePasse} autoComplete="new-password" aide="12 caractères minimum." />
            <TextField label="Confirmer le mot de passe" type="password" required value={d.confirmation} onChange={maj('confirmation')} erreur={erreurs.confirmation} autoComplete="new-password" />
            <Choice label="J’accepte les conditions générales de vente professionnelles et la politique de protection des données." checked={d.accepteConditions} onChange={maj('accepteConditions')} aria-invalid={!!erreurs.accepteConditions || undefined} />
            {erreurs.accepteConditions && <span className="my-field__erreur" role="alert">{erreurs.accepteConditions}</span>}
          </fieldset>
        )}
        <div className="my-ligne my-ligne--espace">
          {etape > 0 ? <Button variante="tertiaire" onClick={() => { setErreurs({}); setEtape(etape - 1); }}>Précédent</Button> : <Link to="/connexion">J’ai déjà un compte</Link>}
          <Button type="submit" chargement={envoi}>{etape < 3 ? 'Continuer' : 'Créer mon compte'}</Button>
        </div>
      </form>
    </Card>
  );
}

function ResultatPreVerification({ pre }: { pre: PreVerification }) {
  if (pre.indisponible) return <Alert ton="alerte" titre="Base Sirene indisponible">Le dossier sera vérifié manuellement après confirmation de votre e-mail.</Alert>;
  if (!pre.formatValide) return <Alert ton="erreur" titre="SIRET non reconnu">Simulation : seuls les SIRET de test (préfixe 000) sont acceptés.</Alert>;
  if (pre.doublon) return <Alert ton="erreur" titre="SIRET déjà inscrit">Un compte existe déjà pour cet établissement. Contactez l’administrateur de votre entreprise.</Alert>;
  if (pre.etablissementActif === false) return <Alert ton="erreur" titre="Établissement inactif">La base Sirene indique un établissement fermé.</Alert>;
  if (pre.nafAutorise === false) return <Alert ton="alerte" titre="Activité non éligible">Code NAF {pre.codeNaf} hors des activités autorisées : la demande sera refusée.</Alert>;
  return <Alert ton="succes" titre="Établissement trouvé">{pre.raisonSociale} · NAF {pre.codeNaf} <MentionSimule>Sirene simulée</MentionSimule></Alert>;
}

// ───────────────────────────────── Vérification d'e-mail ─────────────────────────────────
interface Statut { statut: string; motifRejet?: string; reference?: string }
const verificationsEnCours = new Map<string, Promise<Statut>>();

export function PageVerificationEmail() {
  const { jeton: jetonChemin } = useParams();
  const [params] = useSearchParams();
  const jeton = jetonChemin ?? params.get('jeton') ?? '';
  const [r, setR] = useState<Statut | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  useEffect(() => {
    if (!jeton) return;
    // Une seule vérification par jeton (le double rendu de développement ne doit pas consommer deux fois le lien).
    if (!verificationsEnCours.has(jeton)) verificationsEnCours.set(jeton, post<Statut>('/auth/email/verifier', { jeton }));
    let actif = true;
    verificationsEnCours.get(jeton)!.then((x) => actif && setR(x), (e) => actif && setErreur(e));
    return () => { actif = false; };
  }, [jeton]);

  return (
    <Card as="section" aria-labelledby="titre-verif">
      <div className="my-pile" aria-live="polite">
        <h1 id="titre-verif" className="my-titre-2">Vérification de votre adresse e-mail</h1>
        {!jeton && <Alert ton="erreur" titre="Lien incomplet">Ouvrez le lien reçu par e-mail.</Alert>}
        {jeton && !r && !erreur && <p role="status">Vérification et contrôles automatiques en cours…</p>}
        {erreur ? <Alert ton="erreur" titre="Lien invalide">{messageErreur(erreur)}</Alert> : null}
        {r && <ResultatControles r={r} />}
      </div>
    </Card>
  );
}

function ResultatControles({ r }: { r: Statut }) {
  if (r.statut === 'ACTIVE') {
    return (
      <>
        <Alert ton="succes" titre="Compte professionnel activé">
          Adresse e-mail confirmée. Les contrôles automatiques (SIRET, activité, doublon, e-mail) sont conformes : votre compte {r.reference} est actif.
          Vos prix personnalisés sont visibles dès la connexion.
        </Alert>
        <Link to="/connexion" className="my-btn my-btn--primaire">Se connecter</Link>
      </>
    );
  }
  if (r.statut === 'A_VERIFIER') {
    return <Alert ton="alerte" titre="Dossier en vérification">Les contrôles automatiques n’ont pas pu aboutir (base Sirene indisponible). Notre équipe vérifie votre dossier ; vous serez prévenu par e-mail.</Alert>;
  }
  if (r.statut === 'REJETEE') {
    return <Alert ton="erreur" titre="Demande refusée">{r.motifRejet ?? 'Les contrôles automatiques ne sont pas conformes.'} Contactez-nous si vous pensez qu’il s’agit d’une erreur.</Alert>;
  }
  return <p>Statut : <StatutBadge statut={r.statut} /></p>;
}

// ───────────────────────────────── Suivi de demande ─────────────────────────────────
export function PageStatutInscription() {
  const [params] = useSearchParams();
  const [reference, setReference] = useState(params.get('reference') ?? '');
  const [suivi, setSuivi] = useState(params.get('suivi') ?? '');
  const [r, setR] = useState<Statut | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const consulter = async (e?: FormEvent) => {
    e?.preventDefault();
    setEnvoi(true); setErreur(null);
    try { setR(await post<Statut>('/inscriptions/statut', { reference: reference.trim(), jetonSuivi: suivi.trim() })); } catch (er) { setErreur(er); setR(null); } finally { setEnvoi(false); }
  };
  useEffect(() => { if (params.get('reference') && params.get('suivi')) void consulter(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  return (
    <Card as="section" aria-labelledby="titre-statut">
      <form className="my-form" onSubmit={consulter}>
        <h1 id="titre-statut" className="my-titre-2">Suivre ma demande d’ouverture de compte</h1>
        <TextField label="Référence de la demande" value={reference} onChange={(e) => setReference(e.target.value)} required />
        <TextField label="Code de suivi" value={suivi} onChange={(e) => setSuivi(e.target.value)} required aide="Fourni à la fin de l’inscription." />
        <Button type="submit" chargement={envoi} disabled={!reference || !suivi}>Consulter</Button>
        {erreur ? <Alert ton="erreur">{messageErreur(erreur)}</Alert> : null}
        {r && (r.statut === 'EMAIL_A_VERIFIER'
          ? <Alert ton="info" titre="En attente de confirmation de l’e-mail">Ouvrez le lien de confirmation envoyé à votre adresse.</Alert>
          : <ResultatControles r={r} />)}
      </form>
    </Card>
  );
}

// ───────────────────────────────── Mot de passe oublié / réinitialisation ─────────────────────────────────
export function PageMotDePasseOublie() {
  const [email, setEmail] = useState('');
  const [envoye, setEnvoye] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    setEnvoi(true); setErreur(null);
    try { await post('/auth/mot-de-passe/oubli', { email: email.trim() }); setEnvoye(true); } catch (er) { setErreur(er); } finally { setEnvoi(false); }
  };
  return (
    <Card as="section" aria-labelledby="titre-oubli">
      <form className="my-form" onSubmit={envoyer}>
        <h1 id="titre-oubli" className="my-titre-2">Mot de passe oublié</h1>
        {envoye ? (
          <Alert ton="succes" titre="Demande prise en compte">Si un compte actif correspond à cette adresse, un lien de réinitialisation valable 1 heure vient d’être envoyé.</Alert>
        ) : (
          <>
            <p className="my-secondaire">Indiquez votre adresse e-mail : vous recevrez un lien de réinitialisation.</p>
            {erreur ? <Alert ton="erreur">{messageErreur(erreur)}</Alert> : null}
            <TextField label="Adresse e-mail" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button type="submit" chargement={envoi} disabled={!email}>Envoyer le lien</Button>
          </>
        )}
        <Link to="/connexion">Revenir à la connexion</Link>
      </form>
    </Card>
  );
}

function FormulaireNouveauMotDePasse({ titre, intro, libelle, onValider, avecIdentite }: { titre: string; intro: string; libelle: string; onValider: (mdp: string, identite: { prenom: string; nom: string }) => Promise<void>; avecIdentite?: boolean }) {
  const [mdp, setMdp] = useState('');
  const [conf, setConf] = useState('');
  const [identite, setIdentite] = useState({ prenom: '', nom: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const valider = async (e: FormEvent) => {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (mdp.length < 12) er.mdp = '12 caractères minimum';
    if (conf !== mdp) er.conf = 'Les mots de passe ne correspondent pas';
    setErreurs(er);
    if (Object.keys(er).length) return;
    setEnvoi(true); setErreur(null);
    try { await onValider(mdp, identite); } catch (x) { setErreur(x); } finally { setEnvoi(false); }
  };
  return (
    <Card as="section" aria-labelledby="titre-mdp">
      <form className="my-form" onSubmit={valider} noValidate>
        <h1 id="titre-mdp" className="my-titre-2">{titre}</h1>
        <p className="my-secondaire">{intro}</p>
        {erreur ? <Alert ton="erreur" titre="Action impossible">{messageErreur(erreur)}</Alert> : null}
        {avecIdentite && (
          <div className="my-form-ligne">
            <TextField label="Prénom" value={identite.prenom} onChange={(e) => setIdentite((x) => ({ ...x, prenom: e.target.value }))} autoComplete="given-name" />
            <TextField label="Nom" value={identite.nom} onChange={(e) => setIdentite((x) => ({ ...x, nom: e.target.value }))} autoComplete="family-name" />
          </div>
        )}
        <TextField label="Nouveau mot de passe" type="password" autoComplete="new-password" required value={mdp} onChange={(e) => setMdp(e.target.value)} erreur={erreurs.mdp} aide="12 caractères minimum." />
        <TextField label="Confirmer le mot de passe" type="password" autoComplete="new-password" required value={conf} onChange={(e) => setConf(e.target.value)} erreur={erreurs.conf} />
        <Button type="submit" chargement={envoi}>{libelle}</Button>
      </form>
    </Card>
  );
}

export function PageReinitialiserMotDePasse() {
  const { jeton: j } = useParams();
  const [params] = useSearchParams();
  const jeton = j ?? params.get('jeton') ?? '';
  const nav = useNavigate();
  if (!jeton) return <Alert ton="erreur" titre="Lien incomplet">Ouvrez le lien reçu par e-mail.</Alert>;
  return (
    <FormulaireNouveauMotDePasse
      titre="Choisir un nouveau mot de passe"
      intro="Toutes vos sessions ouvertes seront fermées par sécurité."
      libelle="Enregistrer le mot de passe"
      onValider={async (mdp) => { await post('/auth/mot-de-passe/reinitialiser', { jeton, nouveauMotDePasse: mdp }); nav('/connexion?reinitialise=1'); }}
    />
  );
}

export function PageInvitation() {
  const { jeton: j } = useParams();
  const [params] = useSearchParams();
  const jeton = j ?? params.get('jeton') ?? '';
  const [ok, setOk] = useState(false);
  if (!jeton) return <Alert ton="erreur" titre="Lien incomplet">Ouvrez le lien reçu par e-mail.</Alert>;
  if (ok) {
    return (
      <Card as="section" aria-labelledby="titre-inv-ok">
        <div className="my-pile">
          <h1 id="titre-inv-ok" className="my-titre-2">Compte activé</h1>
          <Alert ton="succes">Votre mot de passe est défini. Les comptes du personnel MYRON devront activer la vérification en deux étapes à la première connexion.</Alert>
          <Link to="/connexion" className="my-btn my-btn--primaire">Se connecter</Link>
        </div>
      </Card>
    );
  }
  return (
    <FormulaireNouveauMotDePasse
      titre="Accepter l’invitation"
      intro="Définissez votre mot de passe pour activer votre compte."
      libelle="Activer mon compte"
      avecIdentite
      onValider={async (mdp, id) => {
        await post('/auth/invitation/accepter', { jeton, motDePasse: mdp, ...(id.prenom ? { prenom: id.prenom } : {}), ...(id.nom ? { nom: id.nom } : {}) });
        setOk(true);
      }}
    />
  );
}
